/**
 * Absolute backend base for Cognitory landing → Cloud Run API calls.
 * Never derived from hostname or subdomain.
 */

const DEFAULT_PRODUCTION_BACKEND_BASE =
  "https://mny-backend-641898304430.us-central1.run.app" as const;

export const COGNITORY_API_USE_SAME_ORIGIN_ENV =
  "VITE_COGNITORY_API_USE_SAME_ORIGIN" as const;

function isTruthyEnvFlag(value: string | undefined): boolean {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return normalized === "true" || normalized === "1";
}

export function resolveCognitoryApiBaseUrl(
  env: Record<string, string | undefined> = import.meta.env as Record<
    string,
    string | undefined
  >,
): string {
  const fromEnv = String(env.VITE_COGNITORY_API_BASE ?? "").trim();
  if (fromEnv.length > 0) {
    return fromEnv.replace(/\/+$/, "");
  }
  if (isTruthyEnvFlag(env[COGNITORY_API_USE_SAME_ORIGIN_ENV])) {
    // Relative /api/* requests on the preview host (Firebase Hosting proxy).
    return "";
  }
  return DEFAULT_PRODUCTION_BACKEND_BASE;
}

export const COGNITORY_V2_ACCESS_PATH = "/api/cognitory/v2/access" as const;
export const COGNITORY_V2_PROVISIONING_SETUP_ACCESS_PATH =
  "/api/cognitory/v2/provisioning-setup/access" as const;
export const COGNITORY_V2_PROVISIONING_CORPUS_BOOTSTRAP_PATH =
  "/api/cognitory/v2/provisioning-setup/corpus-bootstrap" as const;
export const COGNITORY_V2_PROVISIONING_LIBRARY_STATE_PATH =
  "/api/cognitory/v2/provisioning-setup/library-state" as const;
export const COGNITORY_V2_PROVISIONING_PRODUCT_ACTIVATION_READINESS_PATH =
  "/api/cognitory/v2/provisioning-setup/activation-readiness" as const;
export const COGNITORY_V2_PROVISIONING_PRODUCT_INITIAL_ACTIVATION_PATH =
  "/api/cognitory/v2/provisioning-setup/activate" as const;
export const COGNITORY_V2_PLATFORM_ADMIN_SYSTEMS_PATH =
  "/api/cognitory/v2/platform-admin/systems" as const;

export function buildCognitoryPlatformAdminMembersPath(
  systemId: string,
): string {
  return `/api/cognitory/v2/platform-admin/systems/${encodeURIComponent(
    systemId,
  )}/members`;
}
export const COGNITORY_V2_ANSWER_PATH = "/api/cognitory/v2/answer" as const;
export const COGNITORY_V2_LIBRARY_DOCUMENTS_PATH =
  "/api/cognitory/v2/library/documents" as const;
export const COGNITORY_V2_LIBRARY_SOURCE_PATH =
  "/api/cognitory/v2/library/documents" as const;
export const COGNITORY_V2_CITATION_VERIFY_PATH =
  "/api/cognitory/v2/citation-verify" as const;
export const COGNITORY_V2_GOVERNED_SOURCE_VIEW_SESSION_PATH =
  "/api/cognitory/v2/governed-source-view-session" as const;

export function buildCognitoryQuestionAnswerTraceListPath(
  cognitoryId: string,
): string {
  return `/api/cognitory/v2/cognitories/${encodeURIComponent(
    cognitoryId,
  )}/question-answer-traces`;
}

export function buildCognitoryQuestionAnswerTraceDetailPath(input: {
  cognitoryId: string;
  traceRequestId: string;
}): string {
  return `/api/cognitory/v2/cognitories/${encodeURIComponent(
    input.cognitoryId,
  )}/question-answer-traces/${encodeURIComponent(input.traceRequestId)}`;
}

export function buildCognitoryWorkspaceCapabilitiesPath(
  cognitoryId: string,
): string {
  return `/api/cognitory/v2/cognitories/${encodeURIComponent(
    cognitoryId,
  )}/workspace-capabilities`;
}

export function buildCognitoryQuestionAnswerEvaluationSettingsPath(
  cognitoryId: string,
): string {
  return `/api/cognitory/v2/cognitories/${encodeURIComponent(
    cognitoryId,
  )}/question-answer-evaluation-settings`;
}

export function buildCognitoryQuestionAnswerTraceEvaluationPath(input: {
  cognitoryId: string;
  traceRequestId: string;
}): string {
  return `/api/cognitory/v2/cognitories/${encodeURIComponent(
    input.cognitoryId,
  )}/question-answer-traces/${encodeURIComponent(
    input.traceRequestId,
  )}/evaluation`;
}

export function buildCognitoryQuestionAnswerTraceEvaluationExportPath(
  cognitoryId: string,
): string {
  return `/api/cognitory/v2/cognitories/${encodeURIComponent(
    cognitoryId,
  )}/question-answer-trace-evaluations/export`;
}

export function buildCognitoryLibrarySourcePath(documentId: string): string {
  return `${COGNITORY_V2_LIBRARY_SOURCE_PATH}/${encodeURIComponent(
    documentId,
  )}/source`;
}
